/**
 * @tanstack/react-query hooks for the Loans & Advances module
 * (migration 064). Thin wrappers over lib/api/loans.ts so pages stay
 * declarative and caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-payroll.ts / use-change-request.ts:
 *   ["loans", <area>, ...params]
 *
 * Mutations coarse-invalidate the whole "loans" bucket — a transition
 * (approve/disburse/cancel/prepay/write-off) changes both the list's
 * status badges AND the open detail view's schedule + events + outstanding.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as loansApi from "@/lib/api/loans";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import type {
  CreateLoanRequest,
  LoanStatus,
} from "@/lib/api/loans";

export const loanKeys = {
  all: ["loans"] as const,
  list: (status?: LoanStatus, employeeId?: number, excludeSelf?: boolean) =>
    [
      ...loanKeys.all,
      "list",
      status ?? "any",
      employeeId ?? "any",
      excludeSelf ? "excl-self" : "role",
    ] as const,
  detail: (id: number) => [...loanKeys.all, "detail", id] as const,
  summary: (employeeId?: number) =>
    [...loanKeys.all, "summary", employeeId ?? "self"] as const,
};

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: loanKeys.all });
  // The Inbox / tab badges count requested loans (phantom-count fix).
  invalidateApprovalsSummary(qc);
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useLoans(params?: {
  status?: LoanStatus;
  employeeId?: number;
  excludeSelf?: boolean;
}) {
  return useQuery({
    queryKey: loanKeys.list(
      params?.status,
      params?.employeeId,
      params?.excludeSelf,
    ),
    queryFn: ({ signal }) =>
      loansApi.listLoans(
        {
          status: params?.status,
          employee_id: params?.employeeId,
          ...(params?.excludeSelf ? { exclude_self: true } : {}),
        },
        signal,
      ),
  });
}

export function useLoan(id: number, enabled = true) {
  return useQuery({
    queryKey: loanKeys.detail(id),
    queryFn: ({ signal }) => loansApi.getLoan(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useLoanSummary(employeeId?: number, enabled = true) {
  return useQuery({
    queryKey: loanKeys.summary(employeeId),
    queryFn: ({ signal }) => loansApi.getLoanSummary(employeeId, signal),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLoanRequest) => loansApi.createLoan(body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** HR records a loan / advance for an employee (POST /loans/on-behalf). */
export function useCreateLoanOnBehalf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: loansApi.CreateLoanOnBehalfRequest) => loansApi.createLoanOnBehalf(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCancelLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => loansApi.cancelLoan(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useApproveLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => loansApi.approveLoan(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/** The sole finance user approving their OWN loan (explicit, audited). */
export function useSelfApproveLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => loansApi.approveLoan(id, { self_approve: true }),
    onSuccess: () => invalidateAll(qc),
  });
}

/** The sole finance user disbursing their OWN approved loan (explicit, audited). */
export function useSelfDisburseLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => loansApi.disburseLoan(id, { self_disburse: true }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRejectLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; reason: string }) =>
      loansApi.rejectLoan(vars.id, vars.reason),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDisburseLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => loansApi.disburseLoan(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Books catch-up (QA books #14): also refreshes the accounting pages. */
export function usePostLoanDisbursement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; date?: string; selfPost?: boolean }) =>
      loansApi.postLoanDisbursement(vars.id, vars.date, vars.selfPost),
    onSuccess: () => {
      invalidateAll(qc);
      void qc.invalidateQueries({ queryKey: ["accounting"] });
    },
  });
}

export function usePrepayLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; amountPaise: number; note?: string }) =>
      loansApi.prepayLoan(vars.id, vars.amountPaise, vars.note),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useWriteOffLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; note?: string }) =>
      loansApi.writeOffLoan(vars.id, vars.note),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePreviewSchedule() {
  return useMutation({
    mutationFn: (body: {
      principal_paise: number;
      tenure_months: number;
      first_recovery_year?: number;
      first_recovery_month?: number;
    }) => loansApi.previewSchedule(body),
  });
}
